/**
 * Theme Validation Types
 *
 * Shared types for CSS theme parsing, validation, and result reporting.
 *
 * @module
 */
import { Data } from "effect"

// ── Token Types ──────────────────────────────────────────────────────────────

export type TokenType = "css-color" | "css-length" | "font-stack" | "font-provider-var" | "css-shadow"

export interface TokenDef {
  readonly name: string
  readonly type: TokenType
  readonly description: string
  readonly section: string
}

// ── Parsed Tokens ────────────────────────────────────────────────────────────

export interface ParsedToken {
  readonly name: string
  readonly value: string
  readonly line: number
}

export interface ParsedTheme {
  readonly tokens: readonly ParsedToken[]
  readonly hasThemeBlock: boolean
  readonly rawLineCount: number
}

// ── Validation Results ───────────────────────────────────────────────────────

export interface MissingToken {
  readonly _tag: "MissingToken"
  readonly name: string
  readonly type: TokenType
  readonly section: string
  readonly description: string
}

export interface InvalidToken {
  readonly _tag: "InvalidToken"
  readonly name: string
  readonly value: string
  readonly type: TokenType
  readonly section: string
  readonly message: string
  readonly line: number
}

export interface ExtraToken {
  readonly _tag: "ExtraToken"
  readonly name: string
  readonly value: string
  readonly line: number
}

export interface ValidToken {
  readonly _tag: "ValidToken"
  readonly name: string
  readonly value: string
  readonly type: TokenType
  readonly section: string
}

export type TokenIssue = MissingToken | InvalidToken | ExtraToken

export interface ThemeValidationResult {
  readonly isValid: boolean
  readonly missing: readonly MissingToken[]
  readonly invalid: readonly InvalidToken[]
  readonly extra: readonly ExtraToken[]
  readonly valid: readonly ValidToken[]
  readonly issues: readonly TokenIssue[]
  readonly summary: string
}

// ── Errors ───────────────────────────────────────────────────────────────────

export class ThemeParseError extends Data.TaggedError("ThemeParseError")<{
  readonly reason: "no-theme-block" | "malformed-block" | "unclosed-block"
  readonly message: string
  readonly line?: number
}> {}
